import { Handle, Position } from "@xyflow/react";
import { BarChart3, User, X } from "lucide-react";
import type React from "react";
import { useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import { getOwnerInitials, type OwnerNodeData } from "./org-chart-utils";

export function OwnerNode({ data }: { data: OwnerNodeData }) {
  const { owner, isRoot, onRemove, onViewProfile, onViewDashboard } = data;
  const [isDropTarget, setIsDropTarget] = useState(false);

  const initials = getOwnerInitials(owner.firstName, owner.lastName);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setIsDropTarget(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDropTarget(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setIsDropTarget(false);
  }, []);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drop target for drag-and-drop
    <div
      className={cn(
        "group w-[280px] bg-card rounded-lg shadow-sm border border-border overflow-hidden relative",
        isDropTarget && "ring-2 ring-brand-gold shadow-lg",
      )}
      onDoubleClick={() => data.isAdmin && onViewProfile?.(owner.id)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-brand-navy !border-2 !border-white"
      />

      <div className="absolute top-1.5 right-1.5 flex items-center gap-1 z-10">
        {data.isAdmin && onViewProfile && (
          <button
            type="button"
            className="nodrag flex h-5 w-5 items-center justify-center rounded-full bg-muted text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-brand-navy hover:text-white transition-all cursor-pointer"
            onClick={() => onViewProfile(owner.id)}
          >
            <User className="h-3 w-3" />
          </button>
        )}
        {onViewDashboard && (
          <button
            type="button"
            className="nodrag flex h-5 w-5 items-center justify-center rounded-full bg-muted text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-brand-navy hover:text-white transition-all cursor-pointer"
            onClick={() => onViewDashboard(owner.hubspotOwnerId ?? owner.id)}
          >
            <BarChart3 className="h-3 w-3" />
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            className="nodrag flex h-5 w-5 items-center justify-center rounded-full bg-muted text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground transition-all cursor-pointer"
            onClick={() => onRemove(owner.id)}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Accent bar */}
      <div className={cn("h-[3px]", isRoot ? "bg-brand-gold" : "bg-brand-navy")} />

      <div className="p-3 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold text-sm font-semibold">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-sm font-medium text-foreground truncate">
            {owner.firstName} {owner.lastName}
          </p>
          {owner.role && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{owner.role}</p>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-brand-navy !border-2 !border-white"
      />
    </div>
  );
}
